import React from 'react';
import {
  Box,
  Button,
  HeadingText,
  ParagraphText,
  StackLayout,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

const DESKTOP_BUTTON_WIDTH = '328px';

interface GenericErrorScreenProps {
  illustration: string;
  onButtonClick?: () => void;
  title?: string;
  description?: string;
  buttonText?: string;
}

export const GenericErrorScreen = ({
  illustration,
  onButtonClick,
  title,
  description,
  buttonText,
}: GenericErrorScreenProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  const resolvedTitle =
    title ?? formatMessage({ id: 'STR_ERROR_STATE_HEADING_GENERIC' });
  const resolvedDescription =
    description ?? formatMessage({ id: 'STR_ERROR_STATE_BODY_API' });
  const resolvedButtonText = buttonText ?? formatMessage({ id: 'STR_RELOAD' });

  return (
    <Box
      css={{
        minHeight: '100vh',
        background: isMobile
          ? '$surfaceBackgroundPrimary'
          : '$surfaceBackgroundSecondary',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: isMobile ? '$oneAndHalf' : '$two',
      }}
    >
      <Box
        css={
          isMobile
            ? { width: '100%' }
            : {
                width: '708px',
                maxWidth: '100%',
                marginInline: 'auto',
                background: '$surfaceCardSecondary',
                boxShadow: '$card',
                borderRadius: '$medium',
              }
        }
      >
        <StackLayout
          spacing="$twoAndHalf"
          horizontalAlignment="center"
          css={isMobile ? undefined : { padding: '$twoAndHalf' }}
        >
          <Box
            as="img"
            src={illustration}
            alt=""
            width={176}
            height={176}
            css={{ display: 'block' }}
          />

          <StackLayout spacing="$half" horizontalAlignment="center">
            <HeadingText
              size="lg"
              level="2"
              as="h1"
              css={{ textAlign: 'center' }}
            >
              {resolvedTitle}
            </HeadingText>
            <ParagraphText
              css={{
                typography: '$bodyOneSecondary',
                textAlign: 'center',
                color: '$onSurfaceTextSubdued',
              }}
            >
              {resolvedDescription}
            </ParagraphText>
          </StackLayout>

          {onButtonClick && (
            <Button
              type="button"
              size="large"
              priority="primary"
              width="fillContainer"
              css={isMobile ? undefined : { maxWidth: DESKTOP_BUTTON_WIDTH }}
              onClick={onButtonClick}
            >
              {resolvedButtonText}
            </Button>
          )}
        </StackLayout>
      </Box>
    </Box>
  );
};
